import React from "react";

/**
 * Componente Button - Botão reutilizável com diferentes variantes
 *
 * Props:
 * @param {string} variant - Variante do botão: "primary" | "secondary" | "success" | "danger" | "warning"
 * @param {string} size - Tamanho do botão: "sm" | "md" | "lg"
 * @param {boolean} disabled - Se o botão está desabilitado
 * @param {Function} onClick - Função chamada ao clicar
 * @param {string} className - Classes CSS adicionais
 * @param {React.ReactNode} children - Conteúdo do botão
 * @param {string} type - Tipo do botão: "button" | "submit" | "reset"
 */
const Button = ({
  variant = "primary",
  size = "md",
  rounded = "lg", // "none" | "sm" | "md" | "lg" | "xl" | "full"
  disabled = false,
  onClick,
  className = "",
  children,
  type = "button",
  loading = false, // CAPTURAR E REMOVER
  isMobile, // REMOVER DO DOM CASO VÁ JUNTO
  ...props
}) => {
  // Remover loading e isMobile de props (garantido)
  const cleanProps = { ...props };
  delete cleanProps.loading;
  delete cleanProps.isMobile;

  const getVariantClasses = () => {
    const variants = {
      primary:
        "bg-blue-600 hover:bg-blue-700 text-white focus:ring-blue-500 border-blue-600",
      secondary:
        "bg-gray-100 hover:bg-gray-200 text-gray-700 focus:ring-gray-500 border-gray-300",
      success:
        "bg-green-600 hover:bg-green-700 text-white focus:ring-green-500 border-green-600",
      danger:
        "bg-red-600 hover:bg-red-700 text-white focus:ring-red-500 border-red-600",
      warning:
        "bg-yellow-600 hover:bg-yellow-700 text-white focus:ring-yellow-500 border-yellow-600",
    };
    return variants[variant] || variants.primary;
  };

  const getSizeClasses = () => {
    const sizes = {
      sm: "px-3 py-2 text-sm",
      md: "px-4 py-3 text-sm",
      lg: "px-6 py-4 text-base",
    };
    return sizes[size] || sizes.md;
  };

  const getRoundedClasses = () => {
    const radii = {
      none: "",
      sm: "rounded-sm",
      md: "rounded",
      lg: "rounded-lg",
      xl: "rounded-xl",
      full: "rounded-full",
    };
    return radii[rounded] || radii.lg;
  };

  const baseClasses =
    "inline-flex items-center justify-center font-medium border transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 shadow-sm hover:shadow-md active:shadow-inner disabled:opacity-50 disabled:cursor-not-allowed";

  const buttonClasses = `${baseClasses} ${getVariantClasses()} ${getSizeClasses()} ${getRoundedClasses()} ${className}`;

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      className={buttonClasses}
      {...cleanProps}
    >
      {loading ? (
        <span className="flex items-center justify-center gap-2">
          <svg className="animate-spin h-4 w-4 mr-1 text-white" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          {typeof children === "string" ? <span>{children}</span> : children}
        </span>
      ) : (
        children
      )}
    </button>
  );
};

export default Button;
